import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Flex, Form } from 'antd-v5';
import _, { isNil } from 'lodash';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import type { MBCRecord } from '@appTypes/esb';
import type { PaginationRequest } from '@appTypes/esb/advancements/v2/meritBadges/counselors/search';
import type { ESBBaseError } from '@appTypes/esb/errors';
import { programSel } from '@context/duck/selectorsTyped';
import { useMBDict } from '@dict/duck/hooks';
import ErrorBoundary from '@modules/core/components/ErrorBoundary';
import { featureFlags } from '@modules/featureFlags/utils/featureFlags';
import EditCounselorMeritBadgesModal from '@modules/meritBadgeCounselors/common/components/EditCounselorMeritBadgesModal/EditCounselorMeritBadgesModal';
import { goToMBCBulkAssignRoute } from '@modules/meritBadgeCounselors/mbcBulkAssign/duck/actions';
import type { SearchFormState } from '@modules/meritBadgeCounselors/types';
import {
  useGetCompletePersonProfileQuery,
  useLazyPostMBCounselorsSearchQuery,
} from '@modules/rtk/esb-api';
import PageContainer from '@shared/components/Layouts/PageContainer';
import { Program, organizationTypeId } from '@shared/constants';
import { goToRosterRoute } from '@shared/duck/actionsTyped';
import { toastService } from '@toasts';
import { useActiveOrganization } from '@unit/duck/hooks';
import { usePositions } from '@user/duck/hooks';
import { personGuidSel } from '@user/duck/selectors';

import {
  ALL_MERIT_BADGES,
  ANY_COUNCIL_VALUE,
  AvailabilityFilterOptions,
  BOTH_ALL_NO_MERIT_BADGES,
  MBCSearchFields,
  NON_US_VALUE,
  SearchTableActions,
  allowedPositionsMBoption,
  proximityFilterId,
  usersCouncilAdmins,
  usersDistrictAdmins,
  usersLevelCouncil,
  usersLevelDistrict,
} from '../constants';
import {
  defaultSortBy,
  filterMBCSearchResponse,
  generateMBCSearchPayload,
  getPrimaryAddress,
  sortDefaultBySearchType,
} from '../duck/utils';
import MbcSearch from './MbcSearch/MbcSearch';
import MbcTable from './MbcTable/MbcTable';

const MBCSearchPageContainer = () => {
  const enableSortWithAPI = featureFlags.getFlag('SBL_6789_SORT_WITH_API');
  const { hasPositions } = usePositions();
  const hasCouncilLevelPosition = hasPositions(usersLevelCouncil);
  const hasDistrictLevelPosition = hasPositions(usersLevelDistrict);
  const hasDistrictAdminPosition = hasPositions(usersDistrictAdmins);
  const hasCouncilAdminPosition = hasPositions(usersCouncilAdmins);

  const [
    postMbcSearch,
    { isLoading: mbcSearchLoading, isFetching: mbcFetchLoading },
  ] = useLazyPostMBCounselorsSearchQuery();
  const {
    meritBadges: allMeritBadges,
    response: { isLoading: mbDictionaryLoading },
  } = useMBDict({ params: { status: 'all' } });

  const [form] = Form.useForm<SearchFormState>();
  const dispatch = useDispatch();
  const [mbcSearchRecord, setMBCSearchRecord] = useState<MBCRecord[]>([]);
  const [editMmcMemberId, setEditMmcMemberId] = useState<string | null>(null);
  const [editMBsWarning, setEditMBsWarning] = useState<string>('');

  const currentOrg = useActiveOrganization();
  const unitInfo = currentOrg.data;
  const isUnit = unitInfo?.organizationTypeId === organizationTypeId.UNIT;
  const excludedInUnit = ['Key 3 Delegate', 'Registration Inquiry'];
  const updatedAllowedPositions = isUnit
    ? _.difference(allowedPositionsMBoption, excludedInUnit)
    : allowedPositionsMBoption;
  const initialValMBDropdown =
    featureFlags.getFlag('SBL_6790_ENABLE_NO_MB_FILTER') &&
    hasPositions(updatedAllowedPositions)
      ? BOTH_ALL_NO_MERIT_BADGES.value
      : ALL_MERIT_BADGES.value;

  const personGuid = useSelector(personGuidSel) || '';
  const { data: userProfile } = useGetCompletePersonProfileQuery(
    { personGuid: personGuid },
    { skip: !personGuid },
  );
  const userZip = getPrimaryAddress(userProfile, 'zipCode');

  useEffect(() => {
    if (userProfile) {
      form.setFieldValue(MBCSearchFields.ZIP_CODE, userZip);
    }
  }, [form, userProfile, userZip]);

  const handleEditMbc = (memberId: number) => () => {
    setEditMmcMemberId(`${memberId}`);
  };

  const handleCloseEditMbc = () => {
    setEditMmcMemberId(null);
  };

  const onActionClick = (action: SearchTableActions, counselor: MBCRecord) => {
    switch (action) {
      case SearchTableActions.ASSIGN:
        dispatch(
          goToMBCBulkAssignRoute({ counselorMemberId: counselor.memberId }),
        );

        break;

      default:
        break;
    }
  };

  const [pagination, setPagination] = useState({
    perPage: 100,
    currentPage: 1,
    totalRecords: 0,
  });

  const [searchValue, setSearchValue] = useState<SearchFormState>({});

  const searchMBC = async (params: {
    currentPagination: PaginationRequest;
    search?: SearchFormState;
  }) => {
    try {
      const searchVal = params.search ?? searchValue;

      if (!searchVal.zipCode && userZip) {
        searchVal.zipCode = userZip;
      }

      const payload = generateMBCSearchPayload(
        searchVal,
        params.currentPagination,
      );

      const resMbcSearch = await postMbcSearch({
        body: payload,
      }).unwrap();

      const records = resMbcSearch?.records || [];
      const filteredReponse = filterMBCSearchResponse(
        records,
        searchVal.meritBadges,
      );
      if (enableSortWithAPI) {
        setMBCSearchRecord(filteredReponse);
      } else {
        const sortedResponse = sortDefaultBySearchType(
          searchVal,
          filteredReponse,
        );
        setMBCSearchRecord(sortedResponse);
      }

      setPagination({
        perPage: resMbcSearch?.pagination?.perPage ?? 100,
        currentPage: resMbcSearch?.pagination?.currentPage ?? 1,
        totalRecords: resMbcSearch?.pagination?.totalRecords ?? 0,
      });
    } catch (err) {
      const esbError = err as { status: number; data: ESBBaseError };
      if (
        esbError.status === 400 &&
        esbError.data.errorDesc.includes('not have current')
      ) {
        toastService.error(esbError.data.errorDesc);
      } else {
        toastService.error(<FormattedMessage id="shared.errors.generic" />);
      }
    }
  };

  const onChangePage = async (page: number, size: number) => {
    try {
      const paginationValues: PaginationRequest = {
        page: page,
        perPage: size,
        sortBy: defaultSortBy(searchValue),
        sortOrder: 'asc',
      };

      await searchMBC({
        currentPagination: paginationValues,
        search: searchValue,
      });
    } catch {
      toastService.error(<FormattedMessage id="shared.errors.generic" />);
    }
  };

  const onFinish = async (values: SearchFormState) => {
    try {
      setSearchValue(values);

      const paginationValues: PaginationRequest = {
        page: 1,
        perPage: pagination.perPage,
        sortBy: defaultSortBy(values),
        sortOrder: 'asc',
      };
      await searchMBC({
        currentPagination: paginationValues,
        search: values,
      });
    } catch {
      toastService.error(<FormattedMessage id="shared.errors.generic" />);
    }
  };

  const handleEditMBsWarning = useCallback(() => {
    const searchType = form.getFieldValue(MBCSearchFields.ORG_GUID_TYPE);
    const isNonUSorAny =
      searchType === NON_US_VALUE || searchType === ANY_COUNCIL_VALUE;
    let warningContent = '';

    if (
      (hasDistrictLevelPosition ||
        hasCouncilLevelPosition ||
        hasDistrictAdminPosition ||
        hasCouncilAdminPosition) &&
      isNonUSorAny
    ) {
      if (hasDistrictAdminPosition) {
        warningContent =
          'mbcDashboard.mbTable.column.edit.warningMessage.districtAdmin';
      } else if (hasCouncilAdminPosition) {
        warningContent =
          'mbcDashboard.mbTable.column.edit.warningMessage.councilAdmin';
      } else {
        warningContent =
          'mbcDashboard.mbTable.column.edit.warningMessage.default';
      }
    }
    setEditMBsWarning(warningContent);
  }, [
    form,
    hasCouncilAdminPosition,
    hasCouncilLevelPosition,
    hasDistrictAdminPosition,
    hasDistrictLevelPosition,
  ]);

  return (
    <Form
      form={form}
      onFinish={onFinish}
      initialValues={{
        [MBCSearchFields.MERIT_BADGES]: initialValMBDropdown,
        [MBCSearchFields.AVAILABILITY]: AvailabilityFilterOptions.BOTH,
        [MBCSearchFields.PROXIMITY]: proximityFilterId.MILES_5,
        [MBCSearchFields.ZIP_CODE]: userZip,
      }}
    >
      <Flex gap={'middle'} vertical>
        <MbcSearch
          mbcSearchLoading={mbcSearchLoading || mbcFetchLoading}
          mbDictionaryLoading={mbDictionaryLoading}
          allMeritBadges={allMeritBadges}
          editMBsWarning={editMBsWarning}
          handleEditMBsWarning={handleEditMBsWarning}
        />
        <MbcTable
          mbcSearchLoading={mbcSearchLoading || mbcFetchLoading}
          resSearchMBC={mbcSearchRecord}
          onEdit={handleEditMbc}
          onActionClick={onActionClick}
          pagination={pagination}
          setPagination={setPagination}
          onChangePage={onChangePage}
          searchValue={searchValue}
          editMBsWarning={editMBsWarning}
          searchMBC={searchMBC}
        />
        <EditCounselorMeritBadgesModal
          open={!isNil(editMmcMemberId)}
          searchResults={mbcSearchRecord}
          changeSearchResults={setMBCSearchRecord}
          memberId={editMmcMemberId}
          onCloseModal={handleCloseEditMbc}
        />
      </Flex>
    </Form>
  );
};

export const MBCSearchPage = () => {
  const dispatch = useDispatch();
  const unitProgram = useSelector(programSel);

  const isPackOrExploring = useMemo(
    () =>
      unitProgram === Program.CUB_SCOUT || unitProgram === Program.EXPLORING,
    [unitProgram],
  );

  useEffect(() => {
    if (isPackOrExploring) {
      dispatch(goToRosterRoute());
    }
  }, [dispatch, isPackOrExploring]);

  if (!featureFlags.getFlag('SBL_5804_SHOW_MBC_SEARCH_PAGE')) return null;

  return (
    <ErrorBoundary>
      <PageContainer>
        <MBCSearchPageContainer />
      </PageContainer>
    </ErrorBoundary>
  );
};
